import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import './EbookExportModal.css';

// ─── helpers ────────────────────────────────────────────────────────────────

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

async function loadPdfMake() {
  if (window.pdfMake) return window.pdfMake;
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.9/pdfmake.min.js');
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.9/vfs_fonts.min.js');
  return window.pdfMake;
}

// Convert any image URL to a JPEG base64 string for pdfmake.
// Strategy: try direct browser canvas first (fast, works when CDN sends CORS headers),
// then fall back to the server-side proxy (handles CORS-blocked hosts).
// eslint-disable-next-line no-unused-vars
async function toBase64(url) {
  // Step 1: direct canvas load (crossOrigin = anonymous)
  const direct = await new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    // 10s timeout so a slow/dead host doesn't stall the whole export
    const t = setTimeout(() => { img.src = ''; resolve(null); }, 10000);
    img.onload = () => {
      clearTimeout(t);
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve(c.toDataURL('image/jpeg', 0.85));
      } catch { resolve(null); }
    };
    img.onerror = () => { clearTimeout(t); resolve(null); };
    img.src = url;
  });
  if (direct) return direct;

  // Step 2: server-side proxy (bypasses CORS entirely)
  try {
    const res = await fetch(
      `/.netlify/functions/proxy-image?url=${encodeURIComponent(url)}`,
      { signal: AbortSignal.timeout(15000) }
    );
    if (!res.ok) return null;
    const json = await res.json();
    const data = json.data;
    if (!data) return null;

    // pdfmake can't render WebP/AVIF — convert via canvas
    if (data.startsWith('data:image/webp') || data.startsWith('data:image/avif')) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          try {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth;
            c.height = img.naturalHeight;
            c.getContext('2d').drawImage(img, 0, 0);
            resolve(c.toDataURL('image/jpeg', 0.85));
          } catch { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = data;
      });
    }
    return data;
  } catch { return null; }
}

function formatIngredient(ing) {
  let parts = [];
  if (ing.amount) parts.push(formatAmount(ing.amount));
  if (ing.unit && ing.unit !== 'count') parts.push(ing.unit);
  if (ing.ingredient_name) parts.push(ing.ingredient_name);
  const base = parts.join(' ');
  return ing.prep_notes ? `${base}, ${ing.prep_notes}` : base;
}

function formatAmount(amount) {
  if (!amount) return '';
  const n = parseFloat(amount);
  if (n === Math.floor(n)) return String(Math.floor(n));
  const fractions = [
    [0.125, '⅛'], [0.25, '¼'], [0.333, '⅓'], [0.5, '½'],
    [0.667, '⅔'], [0.75, '¾'],
  ];
  const whole = Math.floor(n);
  const frac = n - whole;
  for (const [val, sym] of fractions) {
    if (Math.abs(frac - val) < 0.04) {
      return whole > 0 ? `${whole}${sym}` : sym;
    }
  }
  return n.toFixed(1).replace(/\.0$/, '');
}

function formatTime(mins) {
  if (!mins) return null;
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const CATEGORY_ORDER = ['Main Course', 'Soup', 'Starters', 'Baking', 'Breakfast', 'Dessert'];

// ─── PDF builder ────────────────────────────────────────────────────────────

function buildDocDefinition(recipes, imgMap, allIngredients, allSteps, allTranslations, lang) {
  const t = translations[lang];

  const transMap = {};
  for (const tr of allTranslations) transMap[tr.recipe_id] = tr;

  const ingMap = {};
  for (const ing of allIngredients) {
    if (!ingMap[ing.recipe_id]) ingMap[ing.recipe_id] = [];
    ingMap[ing.recipe_id].push(ing);
  }

  const stepMap = {};
  for (const step of allSteps) {
    if (!stepMap[step.recipe_id]) stepMap[step.recipe_id] = [];
    stepMap[step.recipe_id].push(step);
  }

  // Sort recipes by category then title
  const sorted = [...recipes].sort((a, b) => {
    const ci = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
    return ci !== 0 ? ci : a.title.localeCompare(b.title);
  });

  // Group for TOC
  const byCategory = {};
  for (const r of sorted) {
    if (!byCategory[r.category]) byCategory[r.category] = [];
    byCategory[r.category].push(r);
  }

  const CLAY  = '#b5845e';
  const DARK  = '#1e1e1e';
  const MID   = '#4a4a4a';
  const LIGHT = '#999999';
  const RULE  = '#e0dbd6';

  // ── COVER ────────────────────────────────────────────────────────────────
  const year = String(new Date().getFullYear());
  const coverContent = [
    // Top spacer
    { text: '', margin: [0, 100, 0, 0] },
    // Thin top rule
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 0.5, lineColor: RULE }],
      margin: [0, 0, 0, 6],
    },
    // Thin second rule
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 2.5, lineColor: CLAY }],
      margin: [0, 0, 0, 36],
    },
    // Main title
    {
      text: 'SOLT PANTRY',
      fontSize: 44,
      bold: true,
      alignment: 'center',
      color: DARK,
      characterSpacing: 6,
      margin: [0, 0, 0, 32],
    },
    // Thick rule under title
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 2.5, lineColor: CLAY }],
      margin: [0, 0, 0, 6],
    },
    // Thin rule
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 0.5, lineColor: RULE }],
      margin: [0, 0, 0, 36],
    },
    // Subtitle
    {
      text: lang === 'hu' ? 'Személyes receptgyűjtemény' : 'A personal recipe collection',
      fontSize: 15,
      alignment: 'center',
      color: MID,
      italics: true,
      margin: [0, 0, 0, 16],
    },
    // Recipe count
    {
      text: lang === 'hu'
        ? `${recipes.length} recept`
        : `${recipes.length} recipe${recipes.length !== 1 ? 's' : ''}`,
      fontSize: 11,
      alignment: 'center',
      color: LIGHT,
      margin: [0, 0, 0, 0],
    },
    // Bottom year
    { text: '', margin: [0, 100, 0, 0] },
    {
      text: year,
      fontSize: 10,
      alignment: 'center',
      color: LIGHT,
      characterSpacing: 2,
    },
    { text: '', pageBreak: 'after' },
  ];

  // ── TOC — using pdfmake's built-in auto-TOC so page numbers are real ────────
  // Category dividers and recipe titles below carry tocItem:'mainToc' to register
  // themselves; pdfmake fills in the page numbers in a second rendering pass.
  const tocContent = [
    {
      toc: {
        id: 'mainToc',
        title: {
          stack: [
            {
              text: lang === 'hu' ? 'TARTALOM' : 'CONTENTS',
              fontSize: 22,
              bold: true,
              color: DARK,
              characterSpacing: 3,
              margin: [0, 0, 0, 8],
            },
            {
              canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 2, lineColor: CLAY }],
              margin: [0, 0, 0, 20],
            },
          ],
        },
        // Style for recipe-title entries (category headings use tocStyle on the item itself)
        textStyle: { fontSize: 10.5, color: MID },
        textMargin: [0, 3, 0, 3],
        numberStyle: { fontSize: 9, color: LIGHT, italics: true },
      },
    },
    { text: '', pageBreak: 'after' },
  ];

  // ── RECIPES ──────────────────────────────────────────────────────────────
  const recipeContents = [];
  let prevCategory = null;

  for (const recipe of sorted) {
    const tr = transMap[recipe.id];
    const ingredients = ingMap[recipe.id] || [];
    const steps = stepMap[recipe.id] || [];

    const title = (lang === 'hu' && tr?.title) ? tr.title : recipe.title;
    const description = (lang === 'hu' && tr?.description) ? tr.description : recipe.description;

    // Translated or original ingredients
    let displayIngredients = ingredients;
    if (lang === 'hu' && tr?.ingredients) {
      try {
        const parsed = typeof tr.ingredients === 'string' ? JSON.parse(tr.ingredients) : tr.ingredients;
        if (Array.isArray(parsed) && parsed.length > 0) displayIngredients = parsed;
      } catch {}
    }

    // Translated or original steps
    let displaySteps = steps;
    if (lang === 'hu' && tr?.steps) {
      try {
        const parsed = typeof tr.steps === 'string' ? JSON.parse(tr.steps) : tr.steps;
        if (Array.isArray(parsed) && parsed.length > 0) displaySteps = parsed;
      } catch {}
    }

    // Tips
    let displayTips = recipe.tips || [];
    if (lang === 'hu' && tr?.tips) {
      try {
        const parsed = typeof tr.tips === 'string' ? JSON.parse(tr.tips) : tr.tips;
        if (Array.isArray(parsed) && parsed.length > 0) displayTips = parsed;
      } catch {}
    }

    // ── Category divider page ──────────────────────────────────────────────
    // Strategy: use pageBreak:'after' on real non-empty content items only.
    // pdfmake v0.2.9 reliably honours pageBreak:'after' on outer stack objects.
    if (recipe.category !== prevCategory) {
      const catLabel = (t.categories && t.categories[recipe.category]) ? t.categories[recipe.category] : recipe.category;
      recipeContents.push({
        stack: [
          { text: '', margin: [0, 200, 0, 0] },
          {
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 0.5, lineColor: RULE }],
            margin: [0, 0, 0, 5],
          },
          {
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 2.5, lineColor: CLAY }],
            margin: [0, 0, 0, 28],
          },
          {
            text: catLabel.toUpperCase(),
            fontSize: 30,
            bold: true,
            color: DARK,
            alignment: 'center',
            characterSpacing: 4,
            margin: [0, 0, 0, 28],
            // Register this category as a section heading in the auto-TOC
            tocItem: 'mainToc',
            tocStyle: { bold: true, color: CLAY, fontSize: 10, characterSpacing: 1 },
            tocMargin: [0, 14, 0, 4],
          },
          {
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 2.5, lineColor: CLAY }],
            margin: [0, 0, 0, 5],
          },
          {
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 0.5, lineColor: RULE }],
          },
        ],
        // pageBreak:'after' on the outer stack — forces the first recipe onto a new page.
        // For the very first category, TOC already ended with pageBreak:'after' so this
        // divider renders on its own page naturally and its 'after' pushes the recipe over.
        // For subsequent categories, the previous recipe's trailing item has pageBreak:'after'
        // which puts this divider on a fresh page, and this 'after' pushes the recipe over.
        pageBreak: 'after',
      });
      prevCategory = recipe.category;
    }

    // ── Recipe page ───────────────────────────────────────────────────────
    const imgData = imgMap[recipe.id];

    // Build metadata string
    const metaParts = [];
    if (recipe.prep_time_mins) metaParts.push(`${lang === 'hu' ? 'Előkészítés' : 'Prep'}: ${formatTime(recipe.prep_time_mins)}`);
    if (recipe.cook_time_mins) metaParts.push(`${lang === 'hu' ? 'Főzés' : 'Cook'}: ${formatTime(recipe.cook_time_mins)}`);
    if (recipe.default_servings) {
      const unit = recipe.servings_unit || (lang === 'hu' ? 'adag' : 'servings');
      metaParts.push(`${lang === 'hu' ? 'Adag' : 'Serves'}: ${recipe.default_servings} ${unit}`);
    }
    if (recipe.difficulty) {
      const diffMap = t.difficulty || { easy: 'Easy', medium: 'Medium', hard: 'Hard' };
      metaParts.push(diffMap[recipe.difficulty] || recipe.difficulty);
    }
    if (recipe.cuisine) {
      const cuisineLabel = (t.cuisines && t.cuisines[recipe.cuisine]) ? t.cuisines[recipe.cuisine] : recipe.cuisine;
      metaParts.push(cuisineLabel);
    }

    // ── Header block: title + meta + description ───────────────────────────
    const headerBlock = [
      // Category label (small caps above title)
      {
        text: ((t.categories && t.categories[recipe.category]) ? t.categories[recipe.category] : recipe.category).toUpperCase(),
        fontSize: 8,
        color: CLAY,
        characterSpacing: 2,
        margin: [0, 0, 0, 6],
      },
      // Title — also registers in the auto-TOC
      {
        text: title,
        fontSize: 24,
        bold: true,
        color: DARK,
        margin: [0, 0, 0, 8],
        tocItem: 'mainToc',
        tocStyle: { fontSize: 10.5, color: MID },
        tocMargin: [16, 3, 0, 3],
      },
      // Clay rule under title
      {
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 1.5, lineColor: CLAY }],
        margin: [0, 0, 0, 10],
      },
      // Meta row
      ...(metaParts.length ? [{
        text: metaParts.join('   ·   '),
        fontSize: 9.5,
        color: MID,
        margin: [0, 0, 0, 8],
      }] : []),
      // Dietary tags
      ...(recipe.dietary_tags?.length ? [{
        text: recipe.dietary_tags.map(tag => (t.dietaryTags && t.dietaryTags[tag]) ? t.dietaryTags[tag] : tag).join('  ·  '),
        fontSize: 8.5,
        color: CLAY,
        italics: true,
        margin: [0, 0, 0, 8],
      }] : []),
      // Description
      ...(description ? [{
        text: description,
        fontSize: 10.5,
        color: MID,
        italics: true,
        margin: [0, 0, 0, 0],
        lineHeight: 1.4,
      }] : []),
    ];

    // ── Full-width image ──────────────────────────────────────────────────
    const imageBlock = imgData ? {
      image: imgData,
      width: 455,
      fit: [455, 170],
      margin: [0, 10, 0, 14],
    } : { text: '', margin: [0, 6, 0, 0] };

    // ── Ingredients (linear) ──────────────────────────────────────────────
    const ingSection = {
      stack: [
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 0.5, lineColor: RULE }],
          margin: [0, 0, 0, 10],
        },
        {
          text: (t.ingredients || 'Ingredients').toUpperCase(),
          fontSize: 8,
          bold: true,
          color: CLAY,
          characterSpacing: 1.5,
          margin: [0, 0, 0, 8],
        },
        // Two-column ingredient grid (two items per row)
        ...(() => {
          const rows = [];
          for (let i = 0; i < displayIngredients.length; i += 2) {
            const left = displayIngredients[i];
            const right = displayIngredients[i + 1];
            rows.push({
              columns: [
                {
                  width: '50%',
                  columns: [
                    { text: '-', width: 8, fontSize: 9, color: CLAY },
                    { text: formatIngredient(left), fontSize: 9.5, color: DARK, lineHeight: 1.3 },
                  ],
                  columnGap: 4,
                },
                right ? {
                  width: '50%',
                  columns: [
                    { text: '-', width: 8, fontSize: 9, color: CLAY },
                    { text: formatIngredient(right), fontSize: 9.5, color: DARK, lineHeight: 1.3 },
                  ],
                  columnGap: 4,
                } : { width: '50%', text: '' },
              ],
              columnGap: 20,
              margin: [0, 2, 0, 0],
            });
          }
          return rows;
        })(),
      ],
      margin: [0, 0, 0, 14],
    };

    // ── Method (linear, numbered) ─────────────────────────────────────────
    const methodSection = {
      stack: [
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 0.5, lineColor: RULE }],
          margin: [0, 0, 0, 10],
        },
        {
          text: (t.instructions || 'Method').toUpperCase(),
          fontSize: 8,
          bold: true,
          color: CLAY,
          characterSpacing: 1.5,
          margin: [0, 0, 0, 8],
        },
        ...displaySteps.map((step, i) => ({
          columns: [
            { text: `${i + 1}.`, width: 18, fontSize: 10, bold: true, color: CLAY },
            { text: step.instruction || String(step), fontSize: 10, color: DARK, lineHeight: 1.45 },
          ],
          columnGap: 6,
          margin: [0, 5, 0, 0],
        })),
      ],
      margin: [0, 0, 0, 14],
    };

    // ── Tips ──────────────────────────────────────────────────────────────
    const tipsSection = displayTips.length > 0 ? {
      stack: [
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 455, y2: 0, lineWidth: 0.5, lineColor: RULE }],
          margin: [0, 0, 0, 10],
        },
        {
          text: (t.tipsTitle || 'Tips & tricks').toUpperCase(),
          fontSize: 8,
          bold: true,
          color: CLAY,
          characterSpacing: 1.5,
          margin: [0, 0, 0, 8],
        },
        ...displayTips.map(tip => ({
          columns: [
            { text: '•', width: 10, fontSize: 9, color: CLAY },
            { text: tip, fontSize: 9.5, color: MID, lineHeight: 1.35 },
          ],
          columnGap: 4,
          margin: [0, 3, 0, 0],
        })),
      ],
      margin: [0, 0, 0, 12],
    } : null;

    // ── Source ────────────────────────────────────────────────────────────
    const sourceSection = recipe.source_url ? {
      text: `${t.recipeFrom || 'Source'}: ${recipe.source_url}`,
      fontSize: 8,
      color: LIGHT,
      italics: true,
      margin: [0, 4, 0, 0],
    } : null;

    // Build recipe in correct order: header → image → ingredients → method → tips → source
    // The last item carries pageBreak:'after' to push the next recipe to a new page
    // (except on the very last recipe in the book).
    const idxInSorted = sorted.indexOf(recipe);
    const isLastRecipe = idxInSorted === sorted.length - 1;

    const orderedItems = [
      { stack: headerBlock },
      imageBlock,
      ingSection,
      methodSection,
      ...(tipsSection ? [tipsSection] : []),
      ...(sourceSection ? [sourceSection] : []),
    ];

    // Apply pageBreak:'after' to the last item so the next recipe starts on a fresh page
    if (!isLastRecipe) {
      const last = orderedItems[orderedItems.length - 1];
      orderedItems[orderedItems.length - 1] = { ...last, pageBreak: 'after' };
    }

    recipeContents.push(...orderedItems);
  }

  return {
    pageSize: 'A4',
    pageMargins: [56, 68, 56, 56],

    header: (pageNum) => {
      if (pageNum <= 2) return null;
      return {
        columns: [
          { text: 'Solt Pantry', fontSize: 8, color: LIGHT, italics: true },
          { text: String(pageNum - 2), fontSize: 8, color: LIGHT, alignment: 'right' },
        ],
        margin: [56, 22, 56, 0],
      };
    },

    footer: () => null,

    content: [
      ...coverContent,
      ...tocContent,
      ...recipeContents,
    ],

    defaultStyle: {
      font: 'Roboto',
      fontSize: 10,
      color: DARK,
      lineHeight: 1.3,
    },
  };
}

// ─── Modal component ─────────────────────────────────────────────────────────

function EbookExportModal({ onClose }) {
  const { language } = useLanguage();
  const t = translations[language];

  const [pdfLang, setPdfLang] = useState(language);
  const [scope, setScope] = useState('all'); // 'all' | 'favourites' | 'collection'
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [step, setStep] = useState('choose'); // 'choose' | 'generating' | 'done' | 'error'
  const [progress, setProgress] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Load collections on mount
  useEffect(() => {
    supabase.from('collections').select('id, name, name_hu').order('name').then(({ data }) => {
      if (data && data.length > 0) {
        setCollections(data);
        setSelectedCollectionId(data[0].id);
      }
    });
  }, []);

  async function handleGenerate() {
    setStep('generating');
    try {
      // 1. Load pdfmake
      setProgress(t.ebookLoadingLibrary || 'Loading PDF library…');
      const pdfMake = await loadPdfMake();

      // 2. Fetch all data from Supabase
      setProgress(t.ebookFetchingRecipes || 'Fetching recipes…');
      let recipesQuery = supabase.from('recipes').select('*').order('category').order('title');
      if (scope === 'favourites') {
        recipesQuery = recipesQuery.eq('is_favourite', true);
      } else if (scope === 'collection' && selectedCollectionId) {
        const { data: collRecipes } = await supabase
          .from('collection_recipes')
          .select('recipe_id')
          .eq('collection_id', selectedCollectionId);
        const ids = (collRecipes || []).map(r => r.recipe_id);
        if (ids.length === 0) throw new Error('This collection is empty.');
        recipesQuery = recipesQuery.in('id', ids);
      }
      const [recipesRes, ingredientsRes, stepsRes] = await Promise.all([
        recipesQuery,
        supabase.from('recipe_ingredients').select('*').order('recipe_id').order('sort_order'),
        supabase.from('recipe_steps').select('*').order('recipe_id').order('step_number'),
      ]);

      if (recipesRes.error) throw new Error(recipesRes.error.message);

      const recipes = recipesRes.data || [];
      const allIngredients = ingredientsRes.data || [];
      const allSteps = stepsRes.data || [];

      // 3. Fetch translations if HU
      let allTranslations = [];
      if (pdfLang === 'hu') {
        setProgress(t.ebookFetchingTranslations || 'Fetching translations…');
        const { data } = await supabase
          .from('recipe_translations')
          .select('*')
          .eq('language_code', 'hu');
        allTranslations = data || [];
      }

      // 4. Images — skipped for consistency (some hosts block loading, looks uneven)
      const imgMap = {};

      // 5. Generate PDF
      setProgress(t.ebookGenerating || 'Generating PDF…');
      const docDef = buildDocDefinition(
        recipes, imgMap, allIngredients, allSteps, allTranslations, pdfLang
      );

      const filename = pdfLang === 'hu' ? 'solt-pantry-receptek.pdf' : 'solt-pantry-recipes.pdf';
      pdfMake.createPdf(docDef).download(filename);

      setStep('done');
    } catch (err) {
      console.error(err);
      setErrorMsg(String(err.message || err));
      setStep('error');
    }
  }

  return (
    <div className="ebook-overlay" onClick={onClose}>
      <div className="ebook-modal" onClick={e => e.stopPropagation()}>
        <button className="ebook-close" onClick={onClose} aria-label="Close">×</button>

        <h2 className="ebook-title">{t.ebookTitle || 'Export PDF Cookbook'}</h2>

        {step === 'choose' && (
          <>
            <p className="ebook-desc">
              {t.ebookDesc || 'Export all your recipes as a beautifully formatted PDF cookbook, with a cover page, table of contents, and one page per recipe.'}
            </p>

            <div className="ebook-lang-row">
              <span className="ebook-lang-label">{t.ebookLanguage || 'Language'}</span>
              <div className="ebook-lang-buttons">
                <button
                  className={`ebook-lang-btn${pdfLang === 'en' ? ' ebook-lang-btn--active' : ''}`}
                  onClick={() => setPdfLang('en')}
                >
                  English
                </button>
                <button
                  className={`ebook-lang-btn${pdfLang === 'hu' ? ' ebook-lang-btn--active' : ''}`}
                  onClick={() => setPdfLang('hu')}
                >
                  Magyar
                </button>
              </div>
            </div>

            <div className="ebook-scope-group">
              <label className="ebook-scope-row">
                <input type="radio" name="scope" value="all" checked={scope === 'all'} onChange={() => setScope('all')} className="ebook-favourites-checkbox" />
                <span>{t.ebookScopeAll || 'All recipes'}</span>
              </label>
              <label className="ebook-scope-row">
                <input type="radio" name="scope" value="favourites" checked={scope === 'favourites'} onChange={() => setScope('favourites')} className="ebook-favourites-checkbox" />
                <span>{t.ebookFavouritesOnly || 'Favourites only'}</span>
              </label>
              {collections.length > 0 && (
                <label className="ebook-scope-row">
                  <input type="radio" name="scope" value="collection" checked={scope === 'collection'} onChange={() => setScope('collection')} className="ebook-favourites-checkbox" />
                  <span>{t.ebookScopeCollection || 'Collection'}:</span>
                  <select
                    className="ebook-collection-select"
                    value={selectedCollectionId || ''}
                    onChange={e => { setSelectedCollectionId(Number(e.target.value)); setScope('collection'); }}
                  >
                    {collections.map(c => (
                      <option key={c.id} value={c.id}>
                        {language === 'hu' && c.name_hu ? c.name_hu : c.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <button className="ebook-generate-btn" onClick={handleGenerate}>
              {t.ebookGenerate || 'Generate & Download PDF'}
            </button>
          </>
        )}

        {step === 'generating' && (
          <div className="ebook-progress">
            <div className="ebook-spinner" />
            <p className="ebook-progress-text">{progress}</p>
            <p className="ebook-progress-sub">
              {t.ebookGeneratingSub || 'This may take 15–30 seconds…'}
            </p>
          </div>
        )}

        {step === 'done' && (
          <div className="ebook-done">
            <div className="ebook-done-icon">✓</div>
            <p className="ebook-done-text">
              {t.ebookDone || 'Your PDF has been downloaded!'}
            </p>
            <button className="ebook-generate-btn" onClick={onClose}>
              {t.ebookClose || 'Close'}
            </button>
          </div>
        )}

        {step === 'error' && (
          <div className="ebook-error">
            <p className="ebook-error-text">
              {t.ebookError || 'Something went wrong generating the PDF.'}
            </p>
            <p className="ebook-error-detail">{errorMsg}</p>
            <button className="ebook-generate-btn" onClick={() => setStep('choose')}>
              {t.cancel || 'Try again'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default EbookExportModal;
